import React, { useState } from 'react'
import { useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import {
  Alert,
  Button,
  EnumSpinnerLoaderPosition,
  Icon,
  SpinnerLoader,
  Switch,
  toastStore,
  Tooltip,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { LINK_HELP_SMART_CALL_FLOW } from 'shared/constants/links'
import { logger } from 'shared/lib'
import { useInboxLayerContext } from 'pages/settings/pages/inboxes/context/inboxLayerContext'
import { SmartCallFlowBuilder } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'
import { BusinessHoursStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions'
import { ForwardCallWarningAlert } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/ForwardCall/ui/ForwardCallWarningAlert/ForwardCallWarningAlert.tsx'
import styles from './styles.module.scss'

export const SmartCallFlow = observer(() => {
  const { smartCallEditStore } = useInboxLayerContext()
  const {
    inbox,
    hasActiveCallFlow,
    onActivateInboxWorkflow,
    smartCallFlowBuilderStore,
    isAutoRechargeRequired,
  } = smartCallEditStore

  const shouldDisableSmartCallFlow = !inbox?.number?.voice

  const shouldShowBHWarning =
    !inbox?.origin.business_hours?.is_active &&
    smartCallFlowBuilderStore.actions.find((action) => action instanceof BusinessHoursStore)

  const { pathname } = useLocation()

  const [loading, setLoading] = useState(false)
  const handleInboxWorkflowStatus = async (status: boolean) => {
    try {
      setLoading(true)
      await onActivateInboxWorkflow(status)
    } catch (e) {
      toastStore.add({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
      logger.error(e)
    } finally {
      setLoading(false)
    }
  }
  const handleEdit = () => {
    uiStore.changeRoute({ path: pathname + '/edit' })
  }
  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div className={styles.left}>
          <div className={styles.row}>
            <Typography ariaLabel={'title'} variant={'body-lg-medium'}>
              Smart call flow
            </Typography>
            <Tooltip
              label={
                <>
                  Set up call routing by adding steps <br /> like menus, forwarding, and voicemail.
                  <br />
                  <a href={`${LINK_HELP_SMART_CALL_FLOW}`} target='_blank' rel='noreferrer'>
                    Learn more
                  </a>
                </>
              }
              placement={'top'}
            >
              <Icon
                icon={'questionFull'}
                color={'var(--content-neutral-primary)'}
                fontSize={12}
                padding={2}
              />
            </Tooltip>
          </div>
          <Typography ariaLabel={'desc'} variant={'body-md-regular'} color={'tertiary'}>
            Simplify inbound call handling with flexible flows
          </Typography>
        </div>
        <div className={styles.right}>
          <Switch
            size={'middle'}
            value={hasActiveCallFlow}
            onChange={handleInboxWorkflowStatus}
            disabled={shouldDisableSmartCallFlow || loading || smartCallEditStore.loading}
          />
        </div>
      </div>
      {isAutoRechargeRequired && <ForwardCallWarningAlert />}
      {shouldShowBHWarning && (
        <Alert
          item={{
            type: 'warning',
            desc: 'Business Hours are off. This flow will run using the During Business Hours branch.',
          }}
          className={styles.alert}
        />
      )}
      {shouldDisableSmartCallFlow && (
        <Alert
          item={{
            type: 'warning',
            desc: 'Inbound calls are disabled for this inbox. Enable them to access call flow.',
          }}
          className={styles.alert}
        />
      )}
      {!shouldDisableSmartCallFlow && !hasActiveCallFlow && !smartCallEditStore.loading && (
        <Alert
          item={{
            type: 'warning',
            desc: 'Incoming calls to this inbox are being dropped. Enable call flow to receive them.',
          }}
          className={styles.alert}
        />
      )}
      <div className={styles.flow}>
        {smartCallEditStore.loading ? (
          <SpinnerLoader
            ariaLabel={'SmartCallFlow'}
            position={EnumSpinnerLoaderPosition.Absolute}
          />
        ) : (
          <>
            <SmartCallFlowBuilder store={smartCallFlowBuilderStore} viewOnly fitViewOnChange />
            {!shouldDisableSmartCallFlow && (
              <div className={styles.edit}>
                <Button
                  text={'Edit call flow'}
                  className={styles.editButton}
                  onClick={handleEdit}
                  size={'extraLarge'}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
})
